import React, { useEffect, useState } from 'react';
import { IconButton } from '../common/IconButton';
import LegoContextSummary from '../lego/LegoContextSummary';
import { getImageThumbnail, getImageThumbnailLarge, getImageThumbnailMedium, getImageThumbnailSmall } from '../../../../lib/imageUtilsESM';

let rightTaken = false;
let leftTaken = false;

// Memoized: clusters/galleries render hundreds–thousands of these at once, and parents
// re-render often (hover, selection, filter changes). React.memo skips a thumbnail whose
// props are referentially unchanged. Internal zoom/rotation state still re-renders normally.
export const ImagePreview = React.memo(function ImagePreview({
  url,
  fluid = false, 
  fluidContain = false,
  fluidCover = false, 
  img32 = false, 
  img50 = false, 
  img200 = false, 
  img100 = false, 
  contain = false, 
  cover = false, 
  className = '', 
  selected = false, 
  zoom = false, 
  context = null, 
  left = false, 
  imageId = null,
  /** If set, called on click (after ctrl+open handling) instead of rotate / shift zoom. */
  onImageClick = null,
  /** Called when zoom state changes: { zoomedIn, isLeft, keepZoomOpen }. */
  onStateChange = null,
  /** When true, show the zoom overlay without hover. */
  forceZoomedIn = false,
  /** When true, do not render the inline thumbnail. */
  hideThumb = false,
  /** When hideThumb, controls pinned zoom overlay (otherwise internal state). */
  keepZoomOpen: keepZoomOpenForced = undefined,
  children = null,
  ...other 
}) {
  const [zoomedIn, setZoomedIn] = useState(false);
  const [isLeft, setSide] = useState(left);
  const [rotation, setRotation] = useState(0);
  const [keepZoomOpen, setKeepZoomOpen] = useState(false);

  const overlayOnly = hideThumb && forceZoomedIn;
  const overlayIsLeft = overlayOnly ? left : isLeft;
  const overlayKeepZoomOpen = overlayOnly ? !!keepZoomOpenForced : keepZoomOpen;

  useEffect(() => {
    if (overlayOnly) return;
    onStateChange?.({ zoomedIn, isLeft, keepZoomOpen });
  }, [zoomedIn, isLeft, keepZoomOpen, overlayOnly, onStateChange]);

  // While the hover zoom overlay is open, HOLDING Alt shows it on the other half-screen;
  // releasing Alt returns it to its original side (same flip e.altKey applies at hover start,
  // but momentary). Pinned overlays (keepZoomOpen) keep their explicit move buttons instead.
  useEffect(() => {
    if (overlayOnly || !zoom || !zoomedIn || keepZoomOpen) return undefined;
    let flipped = false;
    const flip = () => {
      leftTaken = !leftTaken;
      rightTaken = !rightTaken;
      setSide((prev) => !prev);
    };
    const onKeyDown = (e) => {
      if (e.key !== 'Alt' || e.repeat || flipped) return;
      e.preventDefault();
      flipped = true;
      flip();
    };
    const onKeyUp = (e) => {
      if (e.key !== 'Alt') return;
      e.preventDefault(); // also keeps the browser from focusing its menu bar
      if (flipped) {
        flipped = false;
        flip();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      if (flipped) flip(); // zoom ended mid-press: restore the side bookkeeping
    };
  }, [overlayOnly, zoom, zoomedIn, keepZoomOpen]);

  // When it is a known url pattern with thumbnails, use thumbnails
  const thumbMedium = getImageThumbnailMedium(url);
  const thumbSmall = getImageThumbnailSmall(url);
  const imageLarge = getImageThumbnailLarge(url);

  let zoomOverlay;

  const unfixZoom = () => {
    setKeepZoomOpen(false);
    closeZoom();
  }

  const closeZoom = () => {
    setZoomedIn(false);
    if(isLeft) {
      leftTaken = false;
    } else {
      rightTaken = false;
    }
  }

  const switchSide = (e) => {
    setSide(!isLeft); 
    e.stopPropagation()
    leftTaken = !leftTaken;
    rightTaken = !rightTaken;
  };

  if ((zoomedIn || forceZoomedIn) && zoom) {
    zoomOverlay = <div className={`HalfscreenImgPreview ${overlayKeepZoomOpen ? 'HalfscreenImgPreview--fixed' : ''} ${overlayIsLeft ? 'HalfscreenImgPreview--left' : ''}`}>
      <img src={thumbMedium}
           style={{ maxHeight: '100%', transform: `rotate(${rotation}deg)` }}
           className={'img-fluid'}
           {...other}
      />
      { overlayKeepZoomOpen ? <div className={'float-bottom mb-4 d-inline-flex rounded align-items-center justify-content-center'} style={{ gap: '20px', backgroundColor: 'rgba(0,0,0,0.5)', width: 'auto' }}>
        { !overlayIsLeft ?<IconButton icon={'west'} level={'info'} className={''} onClick={switchSide}>Move left</IconButton> : <span/> }

        <IconButton icon={'clear'} level={'white'} className={''} onClick={unfixZoom}> Close image </IconButton>

        <IconButton icon={'download'} level={'white'} className={'small'} href={url} target="_blank">Original</IconButton>

        { overlayIsLeft ?<IconButton icon={'east'} level={'info'} className={''} onClick={switchSide}>Move right</IconButton> : <span/> }
      
        {imageId ? <div className={'small monospace text-secondary mr-2'} onClick={(e) => {e.stopPropagation();}} style={{ userSelect: 'text' }}>Img id {imageId}</div> : null}
      </div> : null}
      {context ? <LegoContextSummary className={'float-top mb-4'} context={context}/> : null} 
    </div>;
  }

  let startZooming = (e) => {
    if (overlayOnly) return;
    const autoSide = (isLeft && !leftTaken) ? true : rightTaken;
    !keepZoomOpen && setSide(e.altKey ? !autoSide : autoSide)
    setZoomedIn(true);
    setRotation(0);
  };

  const endZooming = () => {
    if(!keepZoomOpen) {
      closeZoom();
      setSide(left);
    }
  }

  let onClick = (e) => {
    e.stopPropagation()
    if(e.ctrlKey) {
      window.open(url, '_blank')
    } else if(e.shiftKey && zoom) {
      // Pin half-screen zoom (same as default behavior); keep working when onImageClick is used
      setKeepZoomOpen(!keepZoomOpen);
      if(setKeepZoomOpen){
        if(isLeft){
          leftTaken = true;
        } else {
          rightTaken = true;
        }
      } else {
        unfixZoom();
      }
    } else if (onImageClick) {
      onImageClick(e);
    } else if(e.shiftKey) {
      setKeepZoomOpen(!keepZoomOpen);
      if(setKeepZoomOpen){
        if(isLeft){
          leftTaken = true;
        } else {
          rightTaken = true;
        }
      } else {
        unfixZoom();
      }
    } else {
      setRotation(rotation + 90);
    }
  };

  let srcUrl = thumbMedium;
  let imgSizeClass;
  let suffix = cover ? '-cover' : (contain ? '-contain' : '');
  if (fluid){
    imgSizeClass = 'img-fluid'
  } else if (fluidCover) {
    imgSizeClass = 'img-fluid-cover'
  } else if (fluidContain) {
    imgSizeClass = 'img-fluid-contain'
  } else if (img200) {
    imgSizeClass = 'img-200'+suffix
  } else if (img100) {
    imgSizeClass = 'img-100'+suffix
  } else if (img50) {
    imgSizeClass = 'img-50'+suffix
    srcUrl = thumbSmall;
  } else if (img32) {
    imgSizeClass = 'img-32'+suffix
    srcUrl = thumbSmall;
  } else if(other.height) {
    if(other.height <= 100) {
      srcUrl = thumbSmall;
    }
    imgSizeClass = ``
  } else {
    imgSizeClass = `img-med`
  }

  return <span className={`inline-block position-relative`}
               onMouseEnter={overlayOnly ? undefined : startZooming}
               onMouseLeave={overlayOnly ? undefined : endZooming}
               onClick={overlayOnly ? undefined : onClick}>
    {!hideThumb ? <img src={srcUrl}
         loading="lazy"
         className={`${zoom ? 'cursor-zoom-in' : ''} ${imgSizeClass} ${className || ''} ${selected ? 'border border-primary' : ''}`}
         {...other}
    /> : null}
    {children ? <div className="float-bottom mb-05">{children}</div> : null}
    {zoomOverlay}
  </span>;
});
